import { FC, useState, useEffect } from 'react';

import { useRouter } from 'next/router';

import { useMediaQuery } from '@mui/material';

import { Element } from 'react-scroll';
import { useRecoilState } from 'recoil';

import { autoScrollReadMore } from '@utils_iqfu/public/autoScrollReadMore';

import SaleCaseCard from '@components_iqfu/public/sale_case_card/SaleCaseCard';
import SaleCaseLayoutSelector from '@components_iqfu/public/sale_case_layout_selector/SaleCaseLayoutSelector';
import SaleCaseTable from '@components_iqfu/public/sale_case_table/SaleCaseTable';
import SaleCasesAnnotation from '@components_iqfu/public/sale_cases_annotation/SaleCasesAnnotation';

import { saleCaseLayoutState } from '@recoil_iqfu/atoms/public/saleCaseLayout';

import { SaleCaseItem } from '@type-def_iqfu/public/SaleCaseItem';

import MarketHouseIcon from '/public/images/iqfu/public/scrollable_icon.svg';

import { MediaQuery } from '@constants_common';

import styles from './SaleCaseArea.module.scss';

type Props = {
  saleCasesData: SaleCaseItem[];
  //会社詳細では会社名表示しない
  showCompanyName?: boolean;
  //価格系の末端ページでは同ページリンクは表示しない
  showPlace?: boolean;
  //マンション用テーブル、土地戸建テーブルのタイトル表示
  showTableTitle?: boolean;
  //土地だけのテーブル表示用
  onlyLandSaleCases?: boolean;
  onlyPurchaseSaleCases?: boolean;
  pageLabel: string;
};

const SaleCaseArea: FC<Props> = ({
  saleCasesData,
  showCompanyName = true,
  showPlace = true,
  showTableTitle = true,
  onlyLandSaleCases = false,
  onlyPurchaseSaleCases = false,
  pageLabel,
}) => {
  const router = useRouter();

  const [saleCaseLayout, setSaleCaseLayout] = useRecoilState(saleCaseLayoutState);
  const [readMoreButtonClicked, setReadMoreButtonClicked] = useState(false);
  const isMobile = useMediaQuery(MediaQuery.lg, { noSsr: true });
  const isShowReadMoreButton = isMobile ? saleCasesData?.length > 2 : saleCasesData?.length > 6;

  const id = Math.random();
  const mobileReadMoreButtonHandler = () => {
    setReadMoreButtonClicked(true);
    autoScrollReadMore(id);
  };

  useEffect(() => {
    //会社詳細ページのみ買取のPCはテーブル表示にする制御ロジック入る
    if (router.pathname !== '/companies/[company_id]' || !isMobile) {
      setSaleCaseLayout('cards');
    }
  }, [isMobile, router.pathname]);

  if (!saleCasesData?.length) return <></>;

  return (
    <>
      {!isMobile && <SaleCaseLayoutSelector />}

      <div
        id={`container_${id}`}
        className={`${styles.saleCaseInfo_container} ${readMoreButtonClicked ? styles.isScrollable : ''} ${
          isShowReadMoreButton ? '' : styles.isScrollable
        }`}
      >
        <Element name={`target_${id}`} />
        {saleCaseLayout === 'cards' && (
          <div className={styles.saleCase_cards_container}>
            {saleCasesData.map((saleCase: SaleCaseItem, i: number) => {
              return (
                <div key={i} className={styles.sale_case_card_wrapper}>
                  <SaleCaseCard
                    data={saleCase}
                    showCompanyName={showCompanyName}
                    showPlace={showPlace}
                    index={i}
                    pageLabel={pageLabel}
                  />
                </div>
              );
            })}
          </div>
        )}
        {saleCaseLayout === 'table' && (
          <>
            <SaleCaseTable
              saleCasesData={saleCasesData}
              pageLabel={pageLabel}
              showPlace={showPlace}
              showTableTitle={showTableTitle}
              readMoreButtonClicked={readMoreButtonClicked}
              onlyLandSaleCases={onlyLandSaleCases}
              showCompanyName={showCompanyName}
              onlyPurchaseSaleCases={onlyPurchaseSaleCases}
            />
          </>
        )}

        {isShowReadMoreButton && (
          <div className={`${styles.read_more_container} ${readMoreButtonClicked ? styles.isClicked : ''}`}>
            <p className={styles.scrollable_text}>
              <MarketHouseIcon />
              スクロール出来るようになります
            </p>
            <button
              className={`${styles.read_more_button} ${readMoreButtonClicked ? styles.isClicked : ''}`}
              onClick={() => mobileReadMoreButtonHandler()}
            >
              もっと見る
            </button>
          </div>
        )}
      </div>

      <div className={styles.annotation_container}>
        <SaleCasesAnnotation />
      </div>
    </>
  );
};

export default SaleCaseArea;
